/* eslint-disable no-case-declarations */
import { ChartData, ChartTypeRegistry } from 'chart.js';
import {
  AxisChartByString,
  ChartByString,
  ChartDisplayType,
  DataPointSetByString
} from '../chart.models';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'app/store';
import { ChartBar } from 'common/components/charts/chart-bar';
import { ChartLine } from 'common/components/charts/chart-line';
import { getChartType } from '../chartOptionsSlice';
import { chartColours } from 'common/components/charts/chart-colours';
import { ChartDoughnut } from 'common/components/charts/chart-doughnut';

export const ChartStrategyString = ({
  chart
}: {
  chart: AxisChartByString | ChartByString;
}) => {
  const selectedChartType =
    useSelector((state: RootState) => getChartType(state, chart.id)) ??
    chart.defaultChartDisplayType;

  const groupByString = (dataPointSets: DataPointSetByString[]) => {
    const labels = [...new Set(dataPointSets.map((dp) => dp.dataPointString))];

    const dataPointSetNames = [
      ...new Set(dataPointSets.map((dp) => dp.dataPointSetName))
    ];

    const chartData: ChartData<keyof ChartTypeRegistry> = {
      labels,
      datasets: dataPointSetNames.map((dps, i) =>
        toChartDataSet(dps, i, dataPointSets, labels)
      )
    };

    return chartData;
  };

  const groupedChartData = useMemo(() => {
    if (chart.dataPointSets.length > 0) {
      return groupByString(chart.dataPointSets);
    }
  }, [chart.dataPointSets]);

  const tryRenderChart = () => {
    switch (selectedChartType) {
      case ChartDisplayType.STACKED_BAR:
      case ChartDisplayType.BAR:
        const barChart = chart as AxisChartByString;
        return (
          <ChartBar
            title={barChart.title}
            data={groupedChartData as ChartData<'bar'>}
            isStackedX={selectedChartType === ChartDisplayType.STACKED_BAR}
            isStackedY={selectedChartType === ChartDisplayType.STACKED_BAR}
            yAxisFormat={barChart.yAxisFormat}
            tooltipFormat={barChart.tooltipFormat}
            xAxisTitle={barChart.xAxisTitle}
            yAxisTitle={barChart.yAxisTitle}
            yHighestValue={barChart.yHighestValue}
            shouldAnimate={barChart.shouldAnimate}
            key={`${barChart.id}-bar`}
          />
        );
      case ChartDisplayType.LINE:
        const lineChart = chart as AxisChartByString;
        return (
          <ChartLine
            title={lineChart.title}
            data={groupedChartData as ChartData<'line'>}
            isStackedX={true}
            isStackedY={true}
            isLinearX={false}
            legendKeysToIgnore={[]}
            xAxisTitle={lineChart.xAxisTitle}
            yAxisTitle={lineChart.yAxisTitle}
            key={`${lineChart.id}-line`}
          />
        );
      case ChartDisplayType.DOUGHNUT:
        return (
          <ChartDoughnut
            title={chart.title}
            data={groupedChartData as any}
            tooltipFormat={chart.tooltipFormat}
            shouldAnimate={chart.shouldAnimate}
            key={`${chart.id}-doughnut`}
          />
        );
    }
  };

  return groupedChartData ? <>{tryRenderChart()}</> : null;
};

function toChartDataSet(
  dataPointSetName: string,
  index: number,
  dataPointsByStringAndCount: DataPointSetByString[],
  labels: string[]
) {
  const dataPointsByStringAndCountWithZeroValues = labels.map(
    (label) =>
      dataPointsByStringAndCount.find(
        (dataPoint) =>
          dataPoint.dataPointSetName === dataPointSetName &&
          dataPoint.dataPointString === label
      )?.value ?? 0
  );

  const dataSet: any = {
    label: dataPointSetName,
    backgroundColor: chartColours[index],
    fill: true,
    data: dataPointsByStringAndCountWithZeroValues
  };

  return dataSet;
}
